import React, { useMemo } from 'react';
import { useTranslation } from 'next-i18next';
import dynamic from 'next/dynamic';
import { LOCALE_COMMON_PATHS, Namespaces } from 'types/locales';
import { TryScrambleText } from 'utils/text-scrambler';
import { sanitizeTestid } from 'utils/stringSanitization';
import { validateLatitudeAndLongitude } from 'utils/geo_helper';
import { testIds } from 'constants/test-constants';
import type Listing from 'data/listing';

import DetailsTable from '../details-table';
import Description from '../description';
import KeyFacts, { KeyFactsFooter, ListingDetails } from '../key-facts';
import type { ListingAgentInfo } from '../listing-agent';
import type { KeyValueItem } from '../../types';
import { buildAdditionalDetailsFields } from './additional-details';
import styles from './style.module.scss';

const WalkScore = dynamic(() => import('components/walk-score'), {
  loading: () => <div>Loading...</div>,
});

//#region Types
type PropertyDetailsProps = {
  /** The full listing (used for description text, detail tables, extras and locale data). */
  listing: Listing;
  /** The key facts data (type, size, taxes, etc.). */
  listingDetails: ListingDetails;
  /** The formatted property address. */
  address: string;
  /** Whether the content is private (blurred). Defaults to `false`. */
  isPrivate?: boolean;
  /** Whether the text should be scrambled. Defaults to `false`. */
  shouldScramble?: boolean;
  /** Whether to show the walk score alongside the description. Defaults to `false`. */
  showWalkScore?: boolean;
  /** Localized maintenance-fees label for the key facts section. */
  localizedMaintenanceFeesLabel?: string;
  /** The listing agent info shown under the key facts. */
  listingAgent?: {
    expAgentId?: number;
    showMilitaryDetails?: boolean;
  };
  /** Callback fired when the contact-agent button is clicked. */
  onContactAgentClick?: (agentInfo: ListingAgentInfo) => void;
  /** Whether to hide the square footage. Defaults to `false`. */
  hideSquareFootage?: boolean;
  /** When `false`, the Property Address row is omitted. Defaults to `true`. */
  showPropertyAddress?: boolean;
};
//#endregion

//#region Sub Components
function AdditionalDetailsSection({ fields, shouldScramble }: { fields: KeyValueItem[]; shouldScramble: boolean }) {
  const { t } = useTranslation(Namespaces.COMMON);
  return (
    <section>
      {fields.map((field, index) => (
        <div key={index}>
          <span>{t(field.label)}</span>
          &nbsp;
          <span data-testid={sanitizeTestid(field.label) + testIds.keyFacts}>{TryScrambleText(field.value, shouldScramble)}</span>
        </div>
      ))}
    </section>
  );
}
//#endregion

//#region Component
/**
 * PropertyDetails — the redesigned single "Property Details" tab content.
 *
 * Gated behind the `ListingPropertyDetailsRedesign` tenant feature flag. Composes:
 * 1. Description — the listing paragraph (and walk score).
 * 2. Key Facts — the key facts fields merged with the description detail tables.
 * 3. Additional Information — fields sourced from the listing's localized `locale_data`.
 * 4. Extra Features — the listing inclusions (`extras`).
 */
export default function PropertyDetails({
  listing,
  listingDetails,
  address,
  isPrivate = false,
  shouldScramble = false,
  showWalkScore = false,
  localizedMaintenanceFeesLabel,
  listingAgent,
  onContactAgentClick,
  hideSquareFootage = false,
  showPropertyAddress = true,
}: PropertyDetailsProps) {
  const { t } = useTranslation(Namespaces.COMMON);
  const additionalDetails = useMemo(
    () => buildAdditionalDetailsFields(listing.localizedContent, t),
    [listing, t]
  );

  const hasDescription = typeof listing.description === 'string' && listing.description.trim().length > 0;
  const showAiTranslationDisclaimer = listing.localizedContent?.isAiTranslated === true;

  const latitude = listing?.position?.latitude;
  const longitude = listing?.position?.longitude;

  return (
    <div className={styles.component}>
      {/* Description — the heading only shows when a description is available. The walk
          score is rendered separately below Key Facts. */}
      <section className={styles.section}>
        {hasDescription && (
          <h3 className={styles.heading}>{t(LOCALE_COMMON_PATHS.ADDRESS_DESCRIPTION_TAB_LABEL)}</h3>
        )}
        <Description
          listing={listing}
          isPrivate={isPrivate}
          shouldScramble={shouldScramble}
          showWalkScore={false}
          showText
          showDetails={false}
          showExtras={false}
          collapsibleText
        />
      </section>

      {/* Key Facts (key facts fields + description detail fields) */}
      <section className={styles.section}>
        <h3 className={styles.heading}>{t(LOCALE_COMMON_PATHS.ADDRESS_KEY_FACTS_TAB_LABEL)}</h3>
        <div className={styles.fields}>
          <KeyFacts
            listingDetails={listingDetails}
            address={address}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            localizedMaintenanceFeesLabel={localizedMaintenanceFeesLabel}
            listingAgent={listingAgent}
            onContactAgentClick={onContactAgentClick}
            hideSquareFootage={hideSquareFootage}
            showPropertyAddress={showPropertyAddress}
            hideEmptyFields
            hideFooter
          />
          <Description
            listing={listing}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            showText={false}
            showDetails
            showExtras={false}
            hideMlsNumber
            hideEmptyFields
          />
          {/* Property Address + disclaimer + listing agent, placed after all key facts fields */}
          <KeyFactsFooter
            address={address}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            showPropertyAddress={showPropertyAddress}
            listingAgent={listingAgent}
            onContactAgentClick={onContactAgentClick}
          />
        </div>
      </section>

      {/* Walk score — placed after Key Facts */}
      {showWalkScore && latitude != null && longitude != null && validateLatitudeAndLongitude(latitude, longitude) && (
        <section className={styles.section}>
          <WalkScore latitude={latitude} longitude={longitude} />
        </section>
      )}

      {/* Additional Information (from locale data) */}
      {additionalDetails.length > 0 && (
        <section className={styles.section}>
          <h3 className={styles.heading}>{t(LOCALE_COMMON_PATHS.ADDRESS_ADDITIONAL_DETAILS_HEADING)}</h3>
          <div className={styles.fields}>
            <DetailsTable isPrivate={isPrivate}>
              <AdditionalDetailsSection fields={additionalDetails} shouldScramble={shouldScramble} />
            </DetailsTable>
          </div>
        </section>
      )}

      {/* Extra Features (inclusions) */}
      {listing.extras && (
        <section className={styles.section}>
          <Description
            listing={listing}
            isPrivate={isPrivate}
            shouldScramble={shouldScramble}
            showText={false}
            showDetails={false}
            showExtras
            extrasHeadingKey={LOCALE_COMMON_PATHS.ADDRESS_EXTRA_FEATURES_HEADING}
          />
        </section>
      )}

      {showAiTranslationDisclaimer && (
        <p className={styles.aiTranslationDisclaimer} data-testid="ai-translated-disclaimer">
          {t(LOCALE_COMMON_PATHS.ADDRESS_AI_TRANSLATED_DISCLAIMER)}
        </p>
      )}
    </div>
  );
}
//#endregion
